import { Button, Dropdown, MenuProps } from 'antd';
import React, { useState } from 'react';
import { Link, Icon } from '@common/components';

import { PERFORM_TOGGLE_SUBMISSIONS_PAUSED_MUTATION } from '@applicant-flow/graphql';
import { useMutation } from '@common/hooks';
import { buildDialogUrl, createDropdownMenuItem } from '@common/services';
import { MissionControlStaffingRequestType } from '@jobs/graphql';
import { createMessage } from 'works/legacy/lib/message-helper';

type JobActionsDisputeType = Pick<
  MissionControlStaffingRequestType,
  'code' | 'id' | 'canUpdate' | 'submissionsPaused'
>;

const generateJobMenuItems = ({
  job,
  togglePauseStaffingRequest,
}: {
  job: JobActionsDisputeType;
  togglePauseStaffingRequest: () => void;
}): MenuProps['items'] => {
  const { id, canUpdate, submissionsPaused } = job;

  const viewJobDrawerUrl = buildDialogUrl('staffing-request-details-drawer', {
    props: { staffingRequestId: id },
  });
  const editJobDrawerUrl = buildDialogUrl('edit-staffing-request-drawer', {
    props: { staffingRequestId: id },
  });

  return [
    {
      key: '1',
      style: { width: '180px' },
      label: <Link to={viewJobDrawerUrl}>View</Link>,
    },
    ...(createDropdownMenuItem({
      key: '2',
      label: <Link to={editJobDrawerUrl}>Edit</Link>,
      condition: canUpdate.value,
    }) ?? []),
    ...(createDropdownMenuItem({
      key: '3',
      label: (
        <a
          className="toggle-pause-sr-action-button"
          onClick={togglePauseStaffingRequest}
        >
          {submissionsPaused ? 'Resume Submissions' : 'Pause New Submissions'}
        </a>
      ),
      condition: canUpdate.value,
    }) ?? []),
  ];
};

export const JobActionsDropdown = ({
  job,
  refetchQueries,
}: {
  job?: JobActionsDisputeType;
  refetchQueries: string[];
}) => {
  const [submitting, setSubmitting] = useState(false);

  const errorMessage = () => {
    createMessage(
      'error',
      'Staffing Request status update error. Try again later or contact support.'
    );
  };

  const [performTogglePauseStaffingRequest] = useMutation(
    PERFORM_TOGGLE_SUBMISSIONS_PAUSED_MUTATION,
    {
      refetchQueries,
      onError: errorMessage,
      onCompleted: (data) => {
        setSubmitting(false);
        const isPaused =
          data.toggleSubmissionsPaused?.staffingRequest?.submissionsPaused;

        if (isPaused === undefined) {
          errorMessage();
        } else {
          const message = `Staffing Request is now ${isPaused ? 'paused for' : 'accepting'} new submissions`;
          createMessage('success', message);
        }
      },
    }
  );

  const togglePauseStaffingRequest = () => {
    if (submitting || !job?.id) return;

    setSubmitting(true);
    performTogglePauseStaffingRequest({
      variables: {
        staffingRequestId: job.id,
      },
    });
  };

  const menuItems = job
    ? generateJobMenuItems({ job, togglePauseStaffingRequest })
    : [];

  return (
    <Dropdown
      className="job-actions-dropdown"
      menu={{ items: menuItems }}
      trigger={['click']}
    >
      <Button
        type="link"
        shape="circle"
        color="elm"
        style={{ padding: 5 }}
        icon={
          <Icon size="lg" icon="verticalEllipsis" kind="regular" color="elm" />
        }
      />
    </Dropdown>
  );
};
